import { EGameStates } from '@Managers/StateManager/GameStates';
import { IActionResponseData, IActionResponseOutcome } from '@GFTypes/Responses/ActionResponse';
import { IWintabConfigBase } from '@Core/Wintab/Wintab.types';
import { TPoint } from '@GFTypes/Point';
import { IWinConfiguration } from '@Core/Win/WinPresentation.types';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import { ILocalStorageConfiguration } from '@Managers/Storage/LocalStorageManager.types';
import { IPreloaderState } from '@Core/PreloaderScene/PreloaderScene.types';
import {
    IAutoPlayState,
    IFreeGamesAutoPlayState,
    ISpinParameters,
    ITurboModeState,
    ISymbolConfig,
    ISymbolParameters,
    IReelParameters,
    TButtonState,
    IEnvironmentConfiguration,
    IPreloaderConfiguration,
    IWinLevel,
    IWildWheelConfiguration,
    IGambleConfiguration,
    IGambleState,
    ISoundsConfiguration,
    IGamePlayMessages,
    ILockAndRoll,
    IAuthenticateGameConfig,
    IAccount,
    IExpandingSymbolFeatureState,
    IBeforeWinAnimationConfiguration,
    IFeatures,
    ISpinOutcome,
    IJackpotWin,
    IFreeGames,
    IBaseWinPresentationState,
    IDebugWinPresentationModeState,
    ITextVariables,
    ICollectorStore,
    IJackpotCasinoState,
    IBonusBuyInitData
} from '@GFTypes/GameStoreTypes';
import { EOrientation } from '@Managers/Device/UseWindowDimensions.types';
import { EPopNotificationError } from '@Managers/PopClient/UsePopClient.types';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { EActionName } from '@Managers/Events/EActionName';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { IBonusBuyConfig } from '@GFTypes/Responses/AuthenticateResponse';

export const enum EGameStoreKeys {
    GeneralAppState = 'GeneralAppState',
    CurrentGameState = 'currentGameState',
    CurrentGameSubState = 'currentGameSubState',
    PreloaderState = 'PreloaderState',
    CurrentOrientation = 'currentOrientation',
    IsMobile = 'isMobile',
    DevicePixelRatio = 'devicePixelRatio',
    GameVirtualSize = 'gameVirtualSize',
    UIMaxScreenCoverage = 'uiMaxScreenCoverage',
    NumberOfReels = 'numberOfReels',
    NumberOfSymbolsPerReel = 'numberOfSymbolsPerReel',
    NumberOfSpinningSpines = 'numberOfSpinningSpines',
    GameType = 'gameType',
    IsTakeInitiated = 'isTakeInitiated',
    IsTakeCompleted = 'isTakeCompleted',
    SymbolParameters = 'symbolParameters',
    ReelParameters = 'reelParameters',
    SpinParametersSets = 'spinParametersSets',
    SpinParameters = 'spinParameters',
    VisibleSymbols = 'visibleSymbols',
    StartHistorySymbols = 'StartHistorySymbols',
    OriginalSpinResponse = 'originalSpinResponse',
    SpinOutcome = 'SpinOutcome',
    ResumedSpinOutcome = 'ResumedSpinOutcome',
    GambleResponse = 'gambleResponse',
    ButtonState = 'buttonState',
    AutoPlayState = 'autoPlayState',
    FreeGamesAutoPlayState = 'freeGamesAutoPlayState',
    TurboModeState = 'turboModeState',
    BetState = 'betState',
    SoundSettings = 'soundSettings',
    SymbolConfiguration = 'symbolConfiguration',
    EnvironmentConfiguration = 'environmentConfiguration',
    WintabConfiguration = 'wintabConfiguration',
    SoundsConfiguration = 'soundConfiguration',
    PreloaderConfiguration = 'preloaderConfiguration',
    WildWheelConfiguration = 'wildWheelConfiguration',
    WinLevelsConfiguration = 'winLevelsConfiguration',
    WinConfiguration = 'WinConfiguration',
    WaysConfiguration = 'WaysConfiguration',
    LocalStorageConfiguration = 'LocalStorageConfiguration',
    GamePlayMessages = 'GamePlayMessages',
    GambleConfiguration = 'GambleConfiguration',
    GambleState = 'GambleState',
    Account = 'Account',
    AuthenticateGameConfig = 'AuthenticateGameConfig',
    AuthenticateStartSymbols = 'AuthenticateStartSymbols',
    IsFullscreen = 'IsFullscreen',
    WinPresentation = 'WinPresentation',
    TransactionCycleStarted = 'TransactionCycleStarted',
    HighestSymbolCountOnPayLine = 'HighestSymbolCountOnPayLine',
    IsQuickStopInitiated = 'IsQuickStopInitiated',
    IsUIBlocked = 'IsUIBlocked',
    IsPauseInitiated = 'IsPauseInitiated',
    GameHistoryState = 'GameHistoryState',
    BonusBuy = 'BonusBuy',
    ExpandingSymbolState = 'ExpandingSymbolState',
    Features = 'Features',
    IsFreeGamesMode = 'IsFreeGamesMode',
    IsFreeGamesIntro = 'IsFreeGamesIntro',
    IsFreeGamesOutro = 'IsFreeGamesOutro',
    IsLockAndRollMode = 'IsLockAndRollMode',
    FreeGames = 'FreeGames',
    LockAndRoll = 'LockAndRoll',
    LastSpinBet = 'LastSpinBet',
    BeforeWinAnimationConfiguration = 'BeforeWinAnimationConfiguration',
    BaseWinPresentationState = 'BaseWinPresentationState',
    DebugWinPresentationModeState = 'isDebugWinPresentationMode',
    JackpotWinConfiguration = 'JackpotWinConfiguration',
    TextVariables = 'TextVariables',
    IsGameRestored = 'IsGameRestored',
    CurrentSpecialGamesType = 'CurrentSpecialGamesType',
    NextActions = 'NextActions',
    Collectors = 'Collectors',
    JackpotCasinoState = 'JackpotCasinoState'
}

export interface IWinPresentation {
    isSkipped: boolean;
    isTerminated: boolean;
    currentSublevelName: string;
    currentSublevelIndex: number;
    finalSublevelName: string;
    finalSublevelIndex: number;
    currentState: EWinPresentationStates;
    bet: number;
    totalWin: number;
}

export interface ICurrencyValue {
    raw: number;
    normalized: number;
    formatted: string;
}

export interface IBetState {
    value: ICurrencyValue;
    index: number;
    maxIndex: number;
    maxValue: ICurrencyValue;
    betSteps: Array<ICurrencyValue>;
    availableBetSteps: Array<ICurrencyValue>;
    available: boolean;
    currencySymbol: string;
}

export interface ISoundSettings {
    reelStop: boolean;
    gambleShuffle: boolean;
    background: boolean;
    muteAll: boolean;
}

export enum ESoundSettingsKeys {
    reelStop = 'reelStop',
    gambleShuffle = 'gambleShuffle',
    background = 'background',
    muteAll = 'muteAll'
}

export enum EAutoPlayAdvancedSettings {
    stopOnAnyWin = 'stopOnAnyWin',
    stopOnBigWin = 'stopOnBigWin',
    stopOnFreeGameStart = 'stopOnFreeGameStart',
    stopOnFreeGameEnd = 'stopOnFreeGameEnd',
    stopOnSingleWinExeeds = 'stopOnSingleWinExeeds'
}

export interface IErrorState {
    code: string;
    error: string;
    category: EPopNotificationError;
}
export interface IGeneralAppState {
    insufficientFunds: boolean;
    error?: IErrorState;
}

export interface IGameHistoryState {
    actionsCollection: (IActionResponseData & { actionName: EActionName; timeStamp: Date; betIndex: number; startSymbols: number[][] })[];
    spinsHistory: (IActionResponseData & { actionName: EActionName; timeStamp: Date; betIndex: number; startSymbols: number[][] })[];
    spinNr: number;
    isHistoryMode: boolean;
}

export interface IBonusBuy {
    isBonusBuyRound: boolean;
    isDialogOpen: boolean;
    bonusBuyBet: number;
    bonusOption: number;
    bonusBuyInitData: IBonusBuyInitData;
    bonusBuyConfig: IBonusBuyConfig;
}

export interface IGameStoreSchema {
    [EGameStoreKeys.GeneralAppState]: IGeneralAppState;
    [EGameStoreKeys.CurrentGameState]: EGameStates;
    [EGameStoreKeys.CurrentGameSubState]: EGameStates | undefined;
    [EGameStoreKeys.PreloaderState]: IPreloaderState;
    [EGameStoreKeys.CurrentOrientation]: EOrientation;
    [EGameStoreKeys.IsFullscreen]: boolean;
    [EGameStoreKeys.IsMobile]: boolean;
    [EGameStoreKeys.DevicePixelRatio]: number;
    [EGameStoreKeys.GameVirtualSize]: TPoint;
    [EGameStoreKeys.UIMaxScreenCoverage]: number;
    [EGameStoreKeys.NumberOfReels]: number;
    [EGameStoreKeys.NumberOfSymbolsPerReel]: number;
    [EGameStoreKeys.NumberOfSpinningSpines]: number;
    [EGameStoreKeys.GameType]: number;
    [EGameStoreKeys.IsTakeInitiated]: boolean;
    [EGameStoreKeys.IsTakeCompleted]: boolean;
    [EGameStoreKeys.SymbolParameters]: ISymbolParameters;
    [EGameStoreKeys.ReelParameters]: IReelParameters;
    [EGameStoreKeys.SpinParametersSets]: ISpinParameters[];
    [EGameStoreKeys.SpinParameters]: ISpinParameters;
    [EGameStoreKeys.VisibleSymbols]: ReelSymbol[][];
    [EGameStoreKeys.StartHistorySymbols]: number[][];
    [EGameStoreKeys.OriginalSpinResponse]: IActionResponseData;
    [EGameStoreKeys.SpinOutcome]: ISpinOutcome;
    [EGameStoreKeys.ResumedSpinOutcome]: ISpinOutcome | undefined;
    [EGameStoreKeys.GambleResponse]: IActionResponseOutcome;
    [EGameStoreKeys.ButtonState]: TButtonState;
    [EGameStoreKeys.AutoPlayState]: IAutoPlayState;
    [EGameStoreKeys.FreeGamesAutoPlayState]: IFreeGamesAutoPlayState;
    [EGameStoreKeys.TurboModeState]: ITurboModeState;
    [EGameStoreKeys.BetState]: IBetState;
    [EGameStoreKeys.SoundSettings]: ISoundSettings;
    [EGameStoreKeys.SymbolConfiguration]: ISymbolConfig[];
    [EGameStoreKeys.EnvironmentConfiguration]: IEnvironmentConfiguration;
    [EGameStoreKeys.WintabConfiguration]: IWintabConfigBase;
    [EGameStoreKeys.SoundsConfiguration]: ISoundsConfiguration;
    [EGameStoreKeys.PreloaderConfiguration]: IPreloaderConfiguration;
    [EGameStoreKeys.WinLevelsConfiguration]: IWinLevel[];
    [EGameStoreKeys.JackpotWinConfiguration]: IJackpotWin[];
    [EGameStoreKeys.WinConfiguration]: IWinConfiguration;
    [EGameStoreKeys.WaysConfiguration]: any;
    [EGameStoreKeys.LocalStorageConfiguration]: ILocalStorageConfiguration;
    [EGameStoreKeys.GamePlayMessages]: IGamePlayMessages;
    [EGameStoreKeys.WildWheelConfiguration]: IWildWheelConfiguration;
    [EGameStoreKeys.GambleConfiguration]: IGambleConfiguration;
    [EGameStoreKeys.GambleState]: IGambleState;
    [EGameStoreKeys.Account]: IAccount;
    [EGameStoreKeys.AuthenticateGameConfig]: IAuthenticateGameConfig;
    [EGameStoreKeys.AuthenticateStartSymbols]: number[][];
    [EGameStoreKeys.WinPresentation]: IWinPresentation;
    [EGameStoreKeys.TransactionCycleStarted]: boolean;
    [EGameStoreKeys.HighestSymbolCountOnPayLine]: number;
    [EGameStoreKeys.IsQuickStopInitiated]: boolean;
    [EGameStoreKeys.IsUIBlocked]: boolean;
    [EGameStoreKeys.IsPauseInitiated]: boolean;
    [EGameStoreKeys.ExpandingSymbolState]: IExpandingSymbolFeatureState;
    [EGameStoreKeys.Features]: IFeatures;
    [EGameStoreKeys.IsFreeGamesIntro]: boolean;
    [EGameStoreKeys.IsFreeGamesOutro]: boolean;
    [EGameStoreKeys.IsFreeGamesMode]: boolean;
    [EGameStoreKeys.IsLockAndRollMode]: boolean;
    [EGameStoreKeys.FreeGames]: IFreeGames;
    [EGameStoreKeys.LockAndRoll]: ILockAndRoll;
    [EGameStoreKeys.LastSpinBet]: number | undefined;
    [EGameStoreKeys.BeforeWinAnimationConfiguration]: IBeforeWinAnimationConfiguration;
    [EGameStoreKeys.BaseWinPresentationState]: IBaseWinPresentationState;
    [EGameStoreKeys.GameHistoryState]: IGameHistoryState;
    [EGameStoreKeys.BonusBuy]: IBonusBuy;
    [EGameStoreKeys.DebugWinPresentationModeState]: IDebugWinPresentationModeState;
    [EGameStoreKeys.TextVariables]: ITextVariables;
    [EGameStoreKeys.IsGameRestored]: boolean;
    [EGameStoreKeys.CurrentSpecialGamesType]: ESpecialGamesType | undefined;
    [EGameStoreKeys.NextActions]: EActionName[];
    [EGameStoreKeys.Collectors]: ICollectorStore | undefined;
    [EGameStoreKeys.JackpotCasinoState]: IJackpotCasinoState;
}

export type TUILayerGameStoreSchema = Omit<IGameStoreSchema, EGameStoreKeys.VisibleSymbols>;
